import {
  buildUpdatePrescriptionDrugRequest,
  type DuplicateFinalizedPrescriptionDrug,
  type PrescriptionDrugInfoResponse,
  type UpdatePrescriptionDrugRequest,
} from '@/entities/prescription/api/prescription.api';

export const groupDuplicateDrugsByCode = (
  items: DuplicateFinalizedPrescriptionDrug[],
): Map<string, DuplicateFinalizedPrescriptionDrug[]> => {
  const groups = new Map<string, DuplicateFinalizedPrescriptionDrug[]>();
  items.forEach((item) => {
    const code = item.drugCode;
    if (!code) return;
    const list = groups.get(code) ?? [];
    list.push(item);
    groups.set(code, list);
  });
  return groups;
};

export const getDuplicateDrugCodes = (
  items: DuplicateFinalizedPrescriptionDrug[],
): string[] =>
  [...groupDuplicateDrugsByCode(items).entries()]
    .filter(([, group]) => group.length >= 2)
    .map(([code]) => code);

export const findDuplicatedDrugCodesInList = (
  drugs: Array<{ drugCode?: string | null }>,
): string[] => {
  const counts: Record<string, number> = {};
  drugs.forEach((drug) => {
    if (!drug.drugCode) return;
    counts[drug.drugCode] = (counts[drug.drugCode] ?? 0) + 1;
  });
  return Object.keys(counts).filter((code) => counts[code] > 1);
};

export const classifyCrossImageDuplicateDrugs = (
  items: DuplicateFinalizedPrescriptionDrug[],
  currentPrescriptionImageCode: string,
): DuplicateFinalizedPrescriptionDrug[] => {
  const crossImage: DuplicateFinalizedPrescriptionDrug[] = [];

  groupDuplicateDrugsByCode(items).forEach((group) => {
    if (group.length < 2) return;
    if (
      group.some(
        (item) => item.prescriptionImageCode !== currentPrescriptionImageCode,
      )
    ) {
      crossImage.push(...group);
    }
  });

  return crossImage;
};

export const mergeCurrentPageDrugsIntoCrossImageDuplicates = (
  apiDuplicates: DuplicateFinalizedPrescriptionDrug[],
  localDrugs: PrescriptionDrugInfoResponse[],
  currentPrescriptionImageCode: string,
): DuplicateFinalizedPrescriptionDrug[] => {
  if (!apiDuplicates.length) return [];

  const duplicateDrugCodes = new Set(
    apiDuplicates.map((item) => item.drugCode).filter(Boolean),
  );
  const existingMrIds = new Set(
    apiDuplicates.map((item) => item.mrId).filter((mrId) => mrId != null),
  );
  const merged = [...apiDuplicates];

  localDrugs.forEach((drug) => {
    if (!drug.drugCode || !duplicateDrugCodes.has(drug.drugCode)) return;

    const mrId = drug.prescriptionMrId;
    if (mrId != null && existingMrIds.has(mrId)) return;

    merged.push({
      mrId,
      drugCode: drug.drugCode,
      drugName: drug.drugName,
      pharmaCompanyName: drug.pharmaCompanyName,
      prescriptionImageCode: currentPrescriptionImageCode,
      inHospitalQuantity: drug.inHospitalQuantity ?? 0,
      outHospitalQuantity: drug.outHospitalQuantity ?? 0,
      pharmaCompanyId: drug.pharmaCompanyId,
      price: drug.price,
    });

    if (mrId != null) existingMrIds.add(mrId);
  });

  return merged;
};

export const buildCrossImageDuplicateDrugs = (
  apiDuplicates: DuplicateFinalizedPrescriptionDrug[],
  localDrugs: PrescriptionDrugInfoResponse[],
  currentPrescriptionImageCode: string,
): DuplicateFinalizedPrescriptionDrug[] => {
  const merged = mergeCurrentPageDrugsIntoCrossImageDuplicates(
    apiDuplicates,
    localDrugs,
    currentPrescriptionImageCode,
  );
  return classifyCrossImageDuplicateDrugs(
    merged,
    currentPrescriptionImageCode,
  );
};

export const isCrossImageDuplicateQuantityChanged = (
  item: DuplicateFinalizedPrescriptionDrug,
  snapshotItem?: DuplicateFinalizedPrescriptionDrug,
): boolean => {
  if (!snapshotItem) return false;
  const inChanged =
    Number(item.inHospitalQuantity ?? 0) !==
    Number(snapshotItem.inHospitalQuantity ?? 0);
  const outChanged =
    Number(item.outHospitalQuantity ?? 0) !==
    Number(snapshotItem.outHospitalQuantity ?? 0);
  return inChanged || outChanged;
};

export const buildDuplicateDrugPatchPayload = (
  record: DuplicateFinalizedPrescriptionDrug,
  localDrug?: PrescriptionDrugInfoResponse,
): UpdatePrescriptionDrugRequest =>
  buildUpdatePrescriptionDrugRequest({
    drugCode: record.drugCode,
    pharmaCompanyId: localDrug?.pharmaCompanyId ?? record.pharmaCompanyId,
    price: Number(localDrug?.price ?? record.price ?? 0),
    outHospitalQuantity: Number(record.outHospitalQuantity ?? 0),
    inHospitalQuantity: Number(record.inHospitalQuantity ?? 0),
  });

export const getAffectedCrossImageDuplicateCodes = (
  currentPrescriptionImageCode: string,
  deletedMrIds: number[],
  snapshot: DuplicateFinalizedPrescriptionDrug[],
  modifiedItems: DuplicateFinalizedPrescriptionDrug[],
): string[] => {
  const affected = new Set<string>();
  const snapshotByMrId = new Map(
    snapshot.map((item) => [item.mrId, item] as const),
  );

  deletedMrIds.forEach((mrId) => {
    const snapshotItem = snapshotByMrId.get(mrId);
    if (snapshotItem?.prescriptionImageCode) {
      affected.add(snapshotItem.prescriptionImageCode);
    }
  });

  modifiedItems.forEach((item) => {
    if (item.prescriptionImageCode) {
      affected.add(item.prescriptionImageCode);
    }
  });

  return [...new Set([currentPrescriptionImageCode, ...affected].filter(Boolean))];
};
